// Default to same-origin so prod works on both devcockpit.ai and dev.devcockpit.ai
// without requiring cross-origin CORS headers for control-plane fetches.
export const API_BASE = import.meta.env.VITE_API_BASE || '/api';
export const CP_BASE  = import.meta.env.VITE_CP_BASE  || '/cp';

let lastPayloadLogAt = 0;

function tryParseJson(raw: string | null) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function getAuthSnapshot() {
  const legacy = tryParseJson(localStorage.getItem('dc.auth'));
  if (legacy?.token) return legacy;

  const persisted = tryParseJson(localStorage.getItem('auth-storage'));
  const state = persisted?.state || null;
  if (state?.token) {
    const tenant = state?.selectedTenant || legacy?.tenant || 'dev';
    const auth = { token: state.token, tenant };
    try {
      localStorage.setItem('dc.auth', JSON.stringify(auth));
    } catch {
      // ignore
    }
    return auth;
  }
  return legacy;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}) {
  return doFetch<T>(`${API_BASE}${path}`, init);
}

export async function cpFetch<T>(path: string, init: RequestInit = {}) {
  return doFetch<T>(`${CP_BASE}${path}`, init);
}

async function doFetch<T>(url: string, init: RequestInit) {
  const auth = getAuthSnapshot();
  let appId: string | null = auth?.app_id || auth?.appId || null;
  try {
    const lsApp = localStorage.getItem('dc.app_id');
    if (!appId && lsApp) appId = lsApp;
  } catch {
    // ignore
  }
  const debugPayloads = (() => {
    try {
      return localStorage.getItem('dc.debug.payloads') === '1';
    } catch {
      return false;
    }
  })();
  
  // For FormData, don't create a Headers object at all - let browser handle it
  if (init.body instanceof FormData) {
    if (!auth?.token) throw new Error('Not authenticated. Paste a JWT.');
    // For FormData, only add non-Content-Type headers
    const headers = new Headers(init.headers || {});
    headers.set('Authorization', `Bearer ${auth.token}`);
    headers.set('x-tenant-id', auth.tenant || (import.meta.env.VITE_TENANT_DEFAULT || 'dev'));
    if (!headers.has('x-app-id') && appId) {
      headers.set('x-app-id', appId);
    }
    // Do NOT set Content-Type - browser will set multipart/form-data with boundary
    const res = await fetch(url, { ...init, headers });
    if (debugPayloads) {
      const cl = res.headers.get('content-length');
      const now = Date.now();
      const n = cl ? Number(cl) : NaN;
      const isLarge = Number.isFinite(n) && n > 500_000;
      const isError = res.status >= 400;
      const shouldLog = isLarge || isError || (now - lastPayloadLogAt) > 5000;
      if (shouldLog) {
        lastPayloadLogAt = now;
        // eslint-disable-next-line no-console
        console.debug('[doFetch]', { url, method: init?.method || 'GET', status: res.status, contentLength: cl });
      }
    }
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}: ${await res.text()}`);
    const ct = res.headers.get('content-type') || '';
    return (ct.includes('json') ? await res.json() : await res.text()) as T;
  }
  
  // For non-FormData requests, set Content-Type to JSON
  const headers = new Headers(init.headers || {});
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  
  if (!auth?.token) throw new Error('Not authenticated. Paste a JWT.');
  headers.set('Authorization', `Bearer ${auth.token}`);
  headers.set('x-tenant-id', auth.tenant || (import.meta.env.VITE_TENANT_DEFAULT || 'dev'));
  if (!headers.has('x-app-id') && appId) {
    headers.set('x-app-id', appId);
  }
  const res = await fetch(url, { ...init, headers });
  if (debugPayloads) {
    const cl = res.headers.get('content-length');
    const now = Date.now();
    const n = cl ? Number(cl) : NaN;
    const isLarge = Number.isFinite(n) && n > 500_000;
    const isError = res.status >= 400;
    const shouldLog = isLarge || isError || (now - lastPayloadLogAt) > 5000;
    if (shouldLog) {
      lastPayloadLogAt = now;
      // eslint-disable-next-line no-console
      console.debug('[doFetch]', { url, method: init?.method || 'GET', status: res.status, contentLength: cl });
    }
  }
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}: ${await res.text()}`);
  const ct = res.headers.get('content-type') || '';
  return (ct.includes('json') ? await res.json() : await res.text()) as T;
}


